/**
 * ViewAgreement Centralized Styles
 *
 * Professional, consistent styling for the ViewAgreement page including:
 * - Deal Info Panel (sidebar)
 * - Products Table (edit and view modes)
 * - Shared layout and spacing constants
 *
 * Uses theme-aware styling with subtle animations and responsive design.
 */

import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, Grid, InputLabel, Typography } from '@mui/material'
import { alpha, styled, Theme } from '@mui/material/styles'

// ─────────────────────────────────────────────────────────────────────────────
// Layout Constants
// ─────────────────────────────────────────────────────────────────────────────

export const AGREEMENT_LAYOUT = {
  borderRadius: {
    container: 12,
    row: 8,
    field: 4,
  },
  spacing: {
    section: 2,
    row: 1,
    cell: 0.5,
  },
  heights: {
    row: 48,
    header: 40,
    field: 45,
  },
  transitions: {
    fast: '0.15s ease',
    medium: '0.2s ease',
    slow: '0.3s ease',
  },
} as const

// ─────────────────────────────────────────────────────────────────────────────
// Grid Size Configuration (centralized from columns.tsx)
// ─────────────────────────────────────────────────────────────────────────────

export const PRODUCT_GRID_SIZES = {
  // For edit mode - use xl values for lg breakpoint to prevent wrapping
  productName: { xs: 12, sm: 5, md: 5, lg: 2.5, xl: 2.5 },
  description: { xs: 1, sm: 1, md: 1, lg: 0.4, xl: 0.4 },
  qty: { xs: 2, sm: 1, md: 1, lg: 0.4, xl: 0.4 },
  numbers: { xs: 3, sm: 1.5, md: 1.5, lg: 0.8, xl: 0.8 },
  dateRange: { xs: 12, sm: 6, md: 6, lg: 2.2, xl: 2.2 },
  status: { xs: 4, sm: 1.5, md: 1.5, lg: 0.8, xl: 0.8 },
  delete: { xs: 1, sm: 1, md: 1, lg: 0.2, xl: 0.2 },
  // For view mode - consistent across breakpoints
  view: {
    productName: { xs: 12, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
    qty: { xs: 2, sm: 1, md: 1, lg: 0.4, xl: 0.4 },
    numbers: { xs: 3, sm: 1, md: 1, lg: 0.8, xl: 0.8 },
    dateRange: { xs: 12, sm: 2.5, md: 2.5, lg: 2.2, xl: 2.2 },
    status: { xs: 4, sm: 1, md: 1, lg: 0.8, xl: 0.8 },
  },
} as const

// Default grid sizes for columns that don't have grid properties defined
const DEFAULT_GRID_SIZES = { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }

// Helper to get grid sizes with defaults
// Accepts any column object and extracts grid sizes, using xl values for lg breakpoint
export const getGridSizes = (column: Record<string, unknown>, defaults = DEFAULT_GRID_SIZES) => ({
  xs: (column.xs as number) ?? defaults.xs,
  sm: (column.sm as number) ?? defaults.sm,
  md: (column.md as number) ?? defaults.md,
  lg: (column.xl as number) ?? defaults.lg, // Use xl values for lg to prevent wrapping
  xl: (column.xl as number) ?? defaults.xl,
})

// ─────────────────────────────────────────────────────────────────────────────
// Main Layout Container
// ─────────────────────────────────────────────────────────────────────────────

export const AgreementContainer = styled(Grid)(({ theme }) => ({
  transition: `all ${AGREEMENT_LAYOUT.transitions.medium}`,
  backgroundColor: theme.palette.background.default,
  boxSizing: 'border-box',
}))

// ─────────────────────────────────────────────────────────────────────────────
// Deal Info Panel (Sidebar)
// ─────────────────────────────────────────────────────────────────────────────

export const DealInfoPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: AGREEMENT_LAYOUT.borderRadius.container,
  padding: 0,
  height: '100%',
  overflowY: 'auto',
}))

export const DealInfoContent = styled(Grid)(({ theme }) => ({
  borderRadius: AGREEMENT_LAYOUT.borderRadius.container,
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.default,
  height: '100%',
}))

export const DealFieldGroup = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  width: '100%',
}))

export const DealFieldLabel = styled(InputLabel)(({ theme }) => ({
  marginLeft: theme.spacing(1.5),
  fontSize: '10.5px',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  '&.accent': {
    color: 'rgba(249, 180, 45, 1)',
    '&.Mui-focused': { color: 'rgba(249, 180, 45, 1)' },
  },
}))

// ─────────────────────────────────────────────────────────────────────────────
// Products Table Container
// ─────────────────────────────────────────────────────────────────────────────

export const ProductsSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.section,
  backgroundImage: theme.palette.background.image,
  backgroundSize: '10px 10px',
  transition: `height ${AGREEMENT_LAYOUT.transitions.slow}`,
}))

export const ProductTableContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: AGREEMENT_LAYOUT.borderRadius.container,
  overflow: 'hidden',
  backgroundColor: alpha(theme.palette.background.paper, 0.4),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}))

// ─────────────────────────────────────────────────────────────────────────────
// Products Table Header
// ─────────────────────────────────────────────────────────────────────────────

export const ProductTableHeader = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  padding: theme.spacing(1, 1.5),
  minHeight: AGREEMENT_LAYOUT.heights.header,
}))

export const HeaderCell = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: alpha(theme.palette.text.secondary, 0.8),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

// ─────────────────────────────────────────────────────────────────────────────
// Product Row
// ─────────────────────────────────────────────────────────────────────────────

interface ProductRowContainerProps {
  isDragging?: boolean
  isNew?: boolean
}

export const ProductRowContainer = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isDragging' && prop !== 'isNew',
})<ProductRowContainerProps>(({ theme, isDragging, isNew }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: isDragging ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.section,
  padding: theme.spacing(0.75, 1),
  marginTop: 2,
  marginLeft: 4,
  marginRight: 4,
  borderRadius: AGREEMENT_LAYOUT.borderRadius.row,
  transition: `all ${AGREEMENT_LAYOUT.transitions.fast}`,
  border: isNew ? `1px dashed ${alpha(theme.palette.info.main, 0.5)}` : '1px solid transparent',

  '&:first-of-type': {
    marginTop: 4,
  },
  '&:last-of-type': {
    marginBottom: 4,
  },

  '&:hover': {
    backgroundColor: isDragging
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.background.section, 0.9),
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,

    '& .drag-handle': {
      opacity: 1,
    },
  },
}))

// ─────────────────────────────────────────────────────────────────────────────
// Drag Handle
// ─────────────────────────────────────────────────────────────────────────────

export const DragHandle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  marginRight: 4,
  opacity: 0.3,
  cursor: 'grab',
  transition: `opacity ${AGREEMENT_LAYOUT.transitions.fast}`,
  color: theme.palette.text.secondary,

  '&:active': {
    cursor: 'grabbing',
  },
}))

export const DragIcon = styled(DragIndicatorIcon)(() => ({
  fontSize: 18,
  color: 'inherit',
}))

// ─────────────────────────────────────────────────────────────────────────────
// Cell Wrapper
// ─────────────────────────────────────────────────────────────────────────────

export const CellWrapper = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  minHeight: AGREEMENT_LAYOUT.heights.field,
}))

// ─────────────────────────────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────────────────────────────

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6, 4),
  minHeight: 200,
  backgroundColor: alpha(theme.palette.background.section, 0.3),
  borderRadius: AGREEMENT_LAYOUT.borderRadius.container,
  border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
  margin: theme.spacing(1),
}))

export const EmptyStateIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  marginBottom: theme.spacing(2),

  '& svg': {
    fontSize: 32,
    color: alpha(theme.palette.primary.main, 0.6),
  },
}))

export const EmptyStateTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}))

export const EmptyStateSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  maxWidth: 280,
}))

// ─────────────────────────────────────────────────────────────────────────────
// Row Number Badge
// ─────────────────────────────────────────────────────────────────────────────

export const RowNumberBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.text.secondary, 0.1),
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
}))

// ─────────────────────────────────────────────────────────────────────────────
// Status Indicator
// ─────────────────────────────────────────────────────────────────────────────

interface StatusDotProps {
  statusColor?: string
}

export const StatusDot = styled(Box, {
  shouldForwardProp: prop => prop !== 'statusColor',
})<StatusDotProps>(({ theme, statusColor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: statusColor || theme.palette.text.secondary,
  marginRight: theme.spacing(0.75),
  flexShrink: 0,
}))

// ─────────────────────────────────────────────────────────────────────────────
// View Mode Row (read-only)
// ─────────────────────────────────────────────────────────────────────────────

export const ViewRowContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.section,
  padding: theme.spacing(1, 1.5),
  marginTop: 2,
  marginLeft: 4,
  marginRight: 4,
  borderRadius: AGREEMENT_LAYOUT.borderRadius.row,
  transition: `all ${AGREEMENT_LAYOUT.transitions.fast}`,
  border: '1px solid transparent',

  '&:first-of-type': {
    marginTop: 4,
  },
  '&:last-of-type': {
    marginBottom: 4,
  },

  '&:hover': {
    backgroundColor: alpha(theme.palette.background.section, 0.9),
  },
}))

export const ViewCellText = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

export const ViewCellSecondary = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}))

// ─────────────────────────────────────────────────────────────────────────────
// Add Service Button Container
// ─────────────────────────────────────────────────────────────────────────────

export const AddServiceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1, 0),
}))

// ─────────────────────────────────────────────────────────────────────────────
// Product Row Field Styles - Consistent sizing for all edit fields
// ─────────────────────────────────────────────────────────────────────────────

// Base field height for all product row inputs
const FIELD_HEIGHT = 40

// Common styles applied to ALL product row fields
export const productFieldBaseStyles = {
  // Consistent height
  height: FIELD_HEIGHT,
  maxHeight: FIELD_HEIGHT,
  minHeight: FIELD_HEIGHT,
  // Prevent shrinking
  flexShrink: 0,
  // Consistent font
  fontSize: 13,
} as const

// TextField sx prop for product row fields
export const productTextFieldSx = (theme: Theme) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    height: FIELD_HEIGHT,
    minHeight: FIELD_HEIGHT,
    maxHeight: FIELD_HEIGHT,
    fontSize: 13,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
    fontSize: 13,
    color: theme.palette.text.primary,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(12px, 10px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(12px, -6px) scale(0.75)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.5),
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.primary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.divider, 0.15),
    },
  },
})

// Autocomplete (service selector) sx prop
export const productAutocompleteSx = (theme: Theme) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    height: FIELD_HEIGHT,
    minHeight: FIELD_HEIGHT,
    maxHeight: FIELD_HEIGHT,
    padding: '0 8px !important',
    fontSize: 13,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
  },
  '& .MuiAutocomplete-input': {
    padding: '4px 4px !important',
    fontSize: 13,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(8px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(10px, -8px) scale(.90)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.primary,
  },
})

// Select field (Status, Payer, etc) sx prop - compact styling
export const productSelectSx = (theme: Theme) => ({
  width: '100%',
  minWidth: 60,
  '& .MuiInputBase-root': {
    height: FIELD_HEIGHT,
    minHeight: FIELD_HEIGHT,
    maxHeight: FIELD_HEIGHT,
    fontSize: 12,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
  },
  '& .MuiSelect-select': {
    padding: '8px 8px',
    fontSize: 12,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(8px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(10px, -8px) scale(.90)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.primary,
  },
})

// Date range picker sx prop
export const productDateRangeSx = (theme: Theme) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    height: FIELD_HEIGHT,
    minHeight: FIELD_HEIGHT,
    maxHeight: FIELD_HEIGHT,
    fontSize: 12,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
  },
  '& .MuiInputBase-input': {
    padding: '8px 8px',
    fontSize: 12,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(8px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(10px, -8px) scale(.90)',
    },
  },
  '& .MuiMultiInputDateRangeField-separator': {
    display: 'none',
    margin: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.primary,
  },
})

// Money/number field sx prop (price, margin, etc) - compact styling for numeric fields
export const productMoneyFieldSx = (theme: Theme) => ({
  width: '100%',
  minWidth: 55,
  '& .MuiInputBase-root': {
    height: FIELD_HEIGHT,
    minHeight: FIELD_HEIGHT,
    maxHeight: FIELD_HEIGHT,
    fontSize: 12,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
  },
  '& .MuiInputBase-input': {
    // padding: '8px 4px',
    fontSize: 12,
    textAlign: 'left' as const,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(8px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(10px, -8px) scale(.90)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.primary,
  },
})

// Delete button container styling
export const deleteButtonSx = () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: FIELD_HEIGHT,
})

// Legacy compatibility exports
export const productFieldSx = productTextFieldSx
export const datePickerSx = productDateRangeSx

// ─────────────────────────────────────────────────────────────────────────────
// Responsive Breakpoint Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Use xl breakpoint (1536px) as the threshold for compact vs expanded view
// Compact view (Edit button) shows for xs, sm, md, lg
// Full expanded view shows only at xl and above
export const COMPACT_BREAKPOINT = 'xl' as const

// Column visibility by breakpoint
export const getVisibleColumns = (isSmallScreen: boolean, dealType: string) => {
  const isCSA = ['CSA', 'RCSA'].includes(dealType)
  const isIO = dealType === 'IO'

  if (isSmallScreen) {
    // Small screen: show only essential columns
    return ['productName', 'price', 'dateRange', 'deleteButton']
  }

  // Full screen: show all relevant columns based on deal type
  if (isCSA) {
    return [
      'productName',
      'description',
      'price',
      'margin',
      'status',
      'dateRange',
      'grossProfit',
      'netTotal',
      'deleteButton',
    ]
  }

  if (isIO) {
    return [
      'productName',
      'description',
      'qty',
      'price',
      'billedPrice',
      'margin',
      'mediaSpend',
      'status',
      'dateRange',
      'payer',
      'accountUsed',
      'grossProfit',
      'deleteButton',
    ]
  }

  // Default (MSA, etc.)
  return [
    'productName',
    'description',
    'price',
    'billedPrice',
    'margin',
    'mediaSpend',
    'status',
    'dateRange',
    'payer',
    'accountUsed',
    'grossProfit',
    'netTotal',
    'deleteButton',
  ]
}
